import React, { Fragment } from 'react';
import { Box, CircularProgress, Collapse, IconButton, List, ListItem, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import type { MenuProps } from '@mui/material';
import { CheckBox, CheckBoxOutlineBlank, ExpandLess, ExpandMore, MenuBook, DragIndicator } from '@mui/icons-material';
import _ from 'lodash';
import './menu-list.scss';
import i18n from '../../../i18n';
import ButtonComponent from '../../../component/button/button.component';
import { DragDropContext, Draggable, Droppable, type DropResult } from '../../../react-beautiful-dnd-compat';
import { FirstMenu } from '../../../models';
import { DroppableProvided } from 'react-beautiful-dnd';
import { Virtuoso } from 'react-virtuoso';

export default class MenuListComponent extends React.Component<Props, State> {
    public state: State = {
        // 当前展开的项
        expandValue: '',
        // 右键菜单
        isOpen: false,
        // 是否折叠
        isFolded: false,
    }

    // *********************
    // Life Cycle Function
    // *********************

    // *********************
    // Default Function
    // *********************
    handleOnDragEnd = (result: DropResult) => {
        this.props.handleOnDragEnd?.(result, (this.props.menu || []).map(item => item.dataSource));
    }

    /** 如 `[0].[2].[1]` → `[0].[2]`；一级 `[0]` 无父级返回 null */
    private menuIndexParentPath(index: string): string | null {
        const idxs = [...index.matchAll(/\[(\d+)\]/g)].map((m) => parseInt(m[1], 10));
        if (idxs.length < 2) {
            return null;
        }
        idxs.pop();
        return idxs.map((n, i) => (i === 0 ? `[${n}]` : `.[${n}]`)).join('');
    }

    /**
     * 与 `renderMenuList` 相同：按 `filterMenuForDisplay` 后的列表，用路径下标解析到节点的 `key`。
     */
    private resolveMenuItemKeyAtDisplayPath(indexPath: string): string | undefined {
        const isCourseGroup = !!this.props.isCourseGroup;
        const idxs = [...indexPath.matchAll(/\[(\d+)\]/g)].map((m) => parseInt(m[1], 10));
        if (idxs.length === 0) {
            return undefined;
        }
        let list = this.filterMenuForDisplay(this.props.menu || [], isCourseGroup);
        let current: MenuListItem | undefined;
        for (let d = 0; d < idxs.length; d++) {
            current = list[idxs[d]];
            if (!current) {
                return undefined;
            }
            if (d < idxs.length - 1) {
                list = this.filterMenuForDisplay(current.children || [], isCourseGroup);
            }
        }
        return current?.key;
    }

    /**
     * 与 `renderMenuList` 内 `isCourseGroup` 分支一致：先过滤再渲染，便于虚拟列表使用稳定 `totalCount`。
     */
    private filterMenuForDisplay(menu: MenuListItem[], isCourseGroup: boolean): MenuListItem[] {
        if (!isCourseGroup) {
            return menu;
        }
        return menu.filter((item) => {
            if (item.type === 'FirstMenu') {
                let flag = false;
                item.children?.forEach((secondItem) => {
                    secondItem.children?.forEach((dishItem) => {
                        if (dishItem.dataSource.item_type === 3) {
                            flag = true;
                        }
                    });
                });
                return flag;
            }
            if (item.type === 'SecondMenu') {
                return (item.children?.length ?? 0) > 0;
            }
            return true;
        });
    }

    private isFlatLeafMenu(menu: MenuListItem[]): boolean {
        return menu.length > 0 && menu.every((m) => _.isEmpty(m.children));
    }

    /** 纯叶子长列表：固定视口高度，内部由 Virtuoso 滚动 */
    private virtuosoHeightForFlatLeafList(itemCount: number): number {
        const rowPx = 52;
        const maxPx = 520;
        const minPx = 200;
        const visibleRows = Math.min(itemCount, 11);
        return Math.min(maxPx, Math.max(minPx, visibleRows * rowPx + 16));
    }

    /**
     * 非拖拽模式下的一行（含子级 Collapse）；供普通 map 与 Virtuoso 共用。
     */
    private renderNonDragMenuRow(
        item: MenuListItem,
        index: number,
        siblings: MenuListItem[],
        menuIndex: string,
        depth: number,
        depthClassIndex: number,
    ): React.ReactElement {
        const { expandValue, activeContextMenu } = this.state;
        const { activeMenu, isShowCheckBox, isHideContextMenu, isShowSecondTitle } = this.props;
        const isExpandValue =
            expandValue === item.key ||
            Boolean(item.children?.find((secondChildren) => expandValue === secondChildren.key));
        const isActive = activeMenu === item.key;
        const isActiveContextMenu = activeContextMenu?.key === item.key;
        const contextMenu = {
            ...item,
            index,
            isFirst: index === 0,
            isLast: index === siblings.length - 1,
        };
        const newMenuIndex = menuIndex ? `${menuIndex}.[${index}]` : `[${index}]`;
        const rowDepthClass = isShowCheckBox ? `menu-list-row-depth-${depthClassIndex}` : '';
        const rowExpandedClass =
            isShowCheckBox && !_.isEmpty(item.children) && isExpandValue ? 'menu-list-row--expanded' : '';
        const subMenuNestClass = isShowCheckBox ? ` sub-menu--depth-${Math.min(depth + 1, 3)}` : '';
        return (
            <Fragment key={item.key}>
                <ListItem
                    component="div"
                    button
                    onClick={() => {
                        this.handleClick(item, newMenuIndex);
                    }}
                    className={`${isActive ? 'active' : ''} ${isActiveContextMenu ? 'activeContextMenu' : ''} ${rowDepthClass} ${rowExpandedClass} ${item.type === 'LoadingRow' ? 'menu-list-row--loading' : ''}`.trim()}
                    onContextMenu={(event) => {
                        if (isHideContextMenu) {
                            return;
                        }
                        this.handleContextMenu(event, contextMenu);
                    }}
                >
                    {this.renderSelectIconSlot(item)}
                    {isShowSecondTitle && item.secondTitle ? (
                        <>
                            <ListItemText primary={item.title} style={{ flex: 1, marginRight: 10 }} />
                            <ListItemText primary={item.secondTitle} style={{ flex: 1 }} />
                        </>
                    ) : (
                        this.renderListItemPrimary(item, isActive)
                    )}
                    {!_.isEmpty(item.children) && (
                        <>
                            {isExpandValue ? (
                                <ExpandLess className="menu-icon" />
                            ) : (
                                <ExpandMore className="menu-icon" />
                            )}
                        </>
                    )}
                    {!isHideContextMenu && (
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                this.handleContextMenu(e as any, contextMenu);
                            }}
                        >
                            <DragIndicator className="menu-icon" />
                        </IconButton>
                    )}
                </ListItem>
                {!_.isEmpty(item.children) && (
                    <Collapse in={isExpandValue} unmountOnExit>
                        <Box className={`sub-menu${subMenuNestClass}`}>
                            {this.renderMenuList(item.children || [], newMenuIndex, depth + 1)}
                        </Box>
                    </Collapse>
                )}
            </Fragment>
        );
    }

    /**
     * 勾选模式：MUI Checkbox 改为图标按钮，颜色 `var(--fill-primary-300)`（与设计稿「图标-按钮 / 主题色-300」一致）
     */
    renderSelectIconSlot(item: MenuListItem) {
        const { isShowCheckBox, checkboxSelectionIds } = this.props;
        /** LoadingRow / 有子节点：不展示勾选 */
        if (!isShowCheckBox || item.children || item.type === 'LoadingRow') {
            return null;
        }
        const ids =
            checkboxSelectionIds !== undefined ? checkboxSelectionIds : (item.checkedSecondMenuIds ?? []);
        const checked = ids.indexOf(_.get(item, 'originId', -1)) !== -1;
        return (
            <ListItemIcon className="menu-list__select-icon-wrap">
                <IconButton
                    size="small"
                    edge="start"
                    tabIndex={-1}
                    disableRipple
                    className="menu-list__select-toggle"
                    aria-label={checked ? i18n.t('google_text_selected') : i18n.t('google_text_select')}
                    aria-pressed={checked}
                >
                    {checked ? (
                        <CheckBox className="menu-list__select-toggle-icon" />
                    ) : (
                        <CheckBoxOutlineBlank className="menu-list__select-toggle-icon" />
                    )}
                </IconButton>
            </ListItemIcon>
        );
    }

    /**
     * 左侧列表主行：支持右侧桌台数量角标（Figma 1587:21334）；LoadingRow 显示转圈 + 文案。
     */
    renderListItemPrimary(item: MenuListItem, isActive: boolean) {
        if (item.type === 'LoadingRow') {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        width: '100%',
                        minWidth: 0,
                        flex: 1,
                        py: 0.5,
                    }}
                    aria-busy="true"
                    aria-label={i18n.t('general_text_loading')}
                >
                    <CircularProgress size={16} thickness={5} sx={{ color: 'var(--fill-primary-300)', flexShrink: 0 }} />
                    <Typography
                        component="span"
                        variant="body2"
                        sx={{ color: 'var(--text-neutral-600)', textAlign: 'center' }}
                    >
                        {item.title || i18n.t('general_text_loading')}
                    </Typography>
                </Box>
            );
        }
        const hasBadge = typeof item.trailingCount === 'number';
        if (!hasBadge) {
            return <ListItemText primary={item.title} />;
        }
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1, minWidth: 0, flex: 1 }}>
                <ListItemText
                    primary={item.title}
                    sx={{
                        flex: '1 1 auto',
                        minWidth: 0,
                        my: 0,
                        '& .MuiListItemText-primary': {
                            fontWeight: 700,
                            fontSize: '1rem',
                            lineHeight: '24px',
                            color: isActive ? 'var(--text-primary-300)' : 'var(--text-neutral-600)',
                        },
                    }}
                />
                <Typography
                    component='span'
                    variant='subtitle2'
                    sx={{
                        flexShrink: 0,
                        minWidth: 24,
                        px: 0.25,
                        borderRadius: '16px',
                        bgcolor: 'var(--fill-neutral-200)',
                        fontWeight: 700,
                        fontSize: '1rem',
                        lineHeight: '24px',
                        textAlign: 'center',
                        color: isActive ? 'var(--text-primary-300)' : 'var(--text-neutral-600)',
                    }}
                >
                    {item.trailingCount}
                </Typography>
            </Box>
        );
    }

    /**
     * 菜单列表组件渲染
     * @param menu
     * @param menuIndex
     * @param depth 嵌套深度（0=一级）；`isShowCheckBox` 时用于分级背景
     */
    renderMenuList(menu: MenuListItem[], menuIndex: string, depth = 0) {
        const { expandValue, activeContextMenu } = this.state;
        const { activeMenu, isShowCheckBox, isHideContextMenu, isShowSecondTitle, isHideSort, disableDrag, isCourseGroup } =
            this.props;
        const dragEnabled = !isHideSort && !disableDrag;
        const depthClassIndex = Math.min(depth, 3);
        if (dragEnabled) {
            return (
                <List component='nav' disablePadding>
                    <DragDropContext onDragEnd={this.handleOnDragEnd}>
                        <Droppable droppableId='characters'>
                            {
                                (provided: DroppableProvided) => (
                                    <ul className="characters" {...provided.droppableProps} ref={provided.innerRef}>
                                        {
                                            menu.map((item, index) => {
                                                // 如果只展示套餐，则过滤没套餐的菜单
                                                if (isCourseGroup) {
                                                    if (item.type === 'FirstMenu') {
                                                        let flag = false;
                                                        item.children?.forEach(secondItem => {
                                                            secondItem.children?.forEach(dishItem => {
                                                                if (dishItem.dataSource.item_type === 3) flag = true;
                                                            })
                                                        })
                                                        if (!flag) return null;
                                                    }
                                                    if (item.type === 'SecondMenu' && item?.children?.length === 0) return null;
                                                }
                                                // 展开
                                                const isExpandValue = (expandValue === item.key) || Boolean(item.children?.find(secondChildren => {
                                                    return expandValue === secondChildren.key
                                                }))
                                                // 选中
                                                const isActive = activeMenu === item.key;
                                                const isActiveContextMenu = activeContextMenu?.key === item.key;
                                                // 右键菜单
                                                const contextMenu = {
                                                    ...item,
                                                    index,
                                                    isFirst: index === 0,
                                                    isLast: index === menu.length - 1
                                                }
                                                const newMenuIndex = menuIndex ? menuIndex + `.[${index}]` : `[${index}]`;
                                                const rowDepthClass = isShowCheckBox ? `menu-list-row-depth-${depthClassIndex}` : '';
                                                const rowExpandedClass =
                                                    isShowCheckBox && !_.isEmpty(item.children) && isExpandValue
                                                        ? 'menu-list-row--expanded'
                                                        : '';
                                                const subMenuNestClass =
                                                    isShowCheckBox ? ` sub-menu--depth-${Math.min(depth + 1, 3)}` : '';
                                                return (
                                                    <Draggable key={item.key} draggableId={item.key} index={index}>
                                                        {
                                                            (provided) => (

                                                                <Fragment key={item.key} >
                                                                    <ListItem
                                                                        button
                                                                        onClick={() => { this.handleClick(item, newMenuIndex) }}
                                                                        className={`${isActive ? 'active' : ''} ${isActiveContextMenu ? 'activeContextMenu' : ''} ${rowDepthClass} ${rowExpandedClass} ${item.type === 'LoadingRow' ? 'menu-list-row--loading' : ''}`.trim()}
                                                                        onContextMenu={(event) => {
                                                                            if (isHideContextMenu) {
                                                                                return
                                                                            }
                                                                            this.handleContextMenu(event, contextMenu)
                                                                        }}
                                                                        ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                                                                    >
                                                                        {this.renderSelectIconSlot(item)}
                                                                        {
                                                                            (isShowSecondTitle && item.secondTitle) ?
                                                                                <>
                                                                                    <ListItemText primary={item.title} style={{ flex: 1, marginRight: 10 }} />
                                                                                    <ListItemText primary={item.secondTitle} style={{ flex: 1 }} />
                                                                                </>
                                                                                :
                                                                                this.renderListItemPrimary(item, isActive)
                                                                        }
                                                                        {
                                                                            !_.isEmpty(item.children) &&
                                                                            <>
                                                                                {isExpandValue ?
                                                                                    <ExpandLess className='menu-icon' /> :
                                                                                    <ExpandMore className='menu-icon' />}
                                                                            </>
                                                                        }
                                                                        {
                                                                            !isHideContextMenu &&
                                                                            <IconButton
                                                                                size='small'
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation()
                                                                                    this.handleContextMenu(e as any, contextMenu)
                                                                                }}>
                                                                                <DragIndicator className='menu-icon' />
                                                                            </IconButton>
                                                                        }
                                                                    </ListItem>

                                                                    {
                                                                        !_.isEmpty(item.children) &&
                                                                        <Collapse in={isExpandValue} unmountOnExit>
                                                                            <Box className={`sub-menu${subMenuNestClass}`}>
                                                                                {
                                                                                    this.renderMenuList(item.children || [], newMenuIndex, depth + 1)
                                                                                }
                                                                            </Box>
                                                                        </Collapse>
                                                                    }

                                                                </Fragment>
                                                            )
                                                        }
                                                    </Draggable>
                                                )
                                            })
                                        }
                                        {provided.placeholder as React.ReactNode}
                                    </ul>
                                )
                            }
                        </Droppable>
                    </DragDropContext>
                </List>
            );
        }

        const { virtualizeFlatLeavesOver } = this.props;
        const displayMenu = this.filterMenuForDisplay(menu, !!isCourseGroup);
        const threshold = virtualizeFlatLeavesOver ?? 80;
        const useVirtuoso = this.isFlatLeafMenu(displayMenu) && displayMenu.length >= threshold;
        if (useVirtuoso) {
            return (
                <List component="div" disablePadding sx={{ width: '100%', display: 'block' }}>
                    <Virtuoso
                        className="menu-list-virtuoso"
                        style={{
                            height: this.virtuosoHeightForFlatLeafList(displayMenu.length),
                            width: '100%',
                        }}
                        totalCount={displayMenu.length}
                        defaultItemHeight={52}
                        increaseViewportBy={{ top: 120, bottom: 220 }}
                        itemContent={(index) => {
                            const row = displayMenu[index];
                            if (!row) {
                                return null;
                            }
                            return this.renderNonDragMenuRow(
                                row,
                                index,
                                displayMenu,
                                menuIndex,
                                depth,
                                depthClassIndex,
                            );
                        }}
                    />
                </List>
            );
        }

        return (
            <List component='nav' disablePadding>
                {displayMenu.map((item, index) =>
                    this.renderNonDragMenuRow(item, index, displayMenu, menuIndex, depth, depthClassIndex),
                )}
            </List>
        );
    }
    /**
     *  菜单右键事件
     * @param event
     * @param menu
     */
    handleContextMenu(event: React.MouseEvent<HTMLDivElement, MouseEvent>, activeContextMenu: ContextMenuItem) {
        event.preventDefault();
        // 右键一级菜单时，使其二级菜单列表展开
        let expandValue = _.cloneDeep(this.state.expandValue)
        expandValue = activeContextMenu.key
        this.setState({
            isOpen: true,
            anchorEl: event.currentTarget,
            activeContextMenu,
            expandValue,
        })
    }

    /**
     * 菜单点击事件
     * @param menu
     * @param index
     */
    handleClick(menu: MenuListItem, index: string) {
        const { expandValue } = this.state;
        // 父菜单展开
        if (!_.isEmpty(menu.children)) {
            let newExpandValue: string;
            if (menu.key === expandValue) {
                /** 收起子级时不要把 `expandValue` 置空，否则一级仅靠「子 key 匹配」的展开态会一起掉；退回父级 key */
                const parentPath = this.menuIndexParentPath(index);
                const parentKey =
                    parentPath != null ? this.resolveMenuItemKeyAtDisplayPath(parentPath) : undefined;
                newExpandValue = parentKey ?? '';
            } else {
                newExpandValue = menu.key;
            }
            this.setState({
                expandValue: newExpandValue,
            });
        }
        this.props.handleClick?.(menu, index);
    }

    /**
     *  关闭右键菜单
     */
    handleCloseDialogFromMenu() {
        this.setState({
            isOpen: false,
        })
        // 解决视图闪烁延迟赋值
        setTimeout(() => {
            this.setState({ activeContextMenu: undefined })
        }, 100);
    }

    /**
     * 右键菜单点击
     * @param key
     */
    handleMenuClick(key: ContextMenuKey) {
        const { activeContextMenu } = this.state;
        if (key === 'FOLD') {
            this.setState({
                isFolded: true
            })
        }
        if (activeContextMenu) {
            this.props.handleMenuClick?.(key, activeContextMenu, activeContextMenu.index)
        }
        this.handleCloseDialogFromMenu()
    }

    // *********************
    // View
    // *********************
    render() {
        const { menu, otherContextMenuOptions, isHideSort, fullWidth, isHideContextMenu, isHideAddBtn, isHideCopy = true, style, isCourseGroup, isShowCheckBox, addButtonPosition = 'auto', hideContextMenuAdd, renderFooterAdd, surfaceVariant = 'card' } = this.props;
        const { isOpen, anchorEl, activeContextMenu, isFolded } = this.state;
        // 右键菜单
        const contextMenuOptions = [
            {
                label: i18n.t('google_text_newly_increased'),
                key: 'ADD',
                hide: !!hideContextMenuAdd,
            },
            {
                label: i18n.t('google_text_secondary_newly_increased'),
                key: 'SUB_ADD',
                hide: (!activeContextMenu?.children) || (!activeContextMenu)
            },
            // 复制一级大类
            {
                label: i18n.t('google_text_copy'),
                key: 'COPY_FIRST',
                hide: isHideCopy || (!activeContextMenu?.children) || (!activeContextMenu)
            },
            // 复制二级大类
            {
                label: i18n.t('google_text_copy'),
                key: 'COPY_SECOND',
                hide: isHideCopy || (activeContextMenu?.children) || (!activeContextMenu)
            },
            // 复制套餐组
            {
                label: i18n.t('google_text_copy'),
                key: 'COPY_CAMBOGROUP',
                hide: !isCourseGroup
            },
            // 移动套餐组
            {
                label: i18n.t('google_text_transfer'),
                key: 'TRANSFER_CAMBOGROUP',
                hide: !isCourseGroup
            },
            {
                label: i18n.t('google_text_public_button_delete'),
                key: 'DELETE',
                hide: menu.length === 0
            },
            {
                label: i18n.t('google_text_move_up'),
                key: 'UP',
                hide: isHideSort || ((activeContextMenu?.isFirst) || (!activeContextMenu))
            },
            {
                label: i18n.t('google_text_move_down'),
                key: 'DOWN',
                hide: isHideSort || ((activeContextMenu?.isLast) || (!activeContextMenu))
            },
            {
                label: i18n.t('google_text_fold_up'),
                key: 'FOLD',
            },
            ...(otherContextMenuOptions || [])
        ]
        return (
            <Box
                className={`common-menu-list ${fullWidth ? 'fullWidth' : ''} ${isFolded ? 'isFolded' : ''} ${isShowCheckBox ? 'common-menu-list--checkbox-mode' : ''}`}
                style={{ ...style }}
                sx={(theme) => {
                    const isPlain = surfaceVariant === 'plain';
                    return {
                        backgroundColor: isFolded ? 'unset' : (isPlain ? 'transparent' : theme.palette.background.paper),
                        boxShadow: isFolded || isPlain ? 'unset' :
                            theme.palette.mode === 'light' ?
                                'rgba(51 48 60 / 3%) 0px 3px 9px 1px, rgb(51 48 60 / 2%) 0px 8px 9px 0px, rgba(51 48 60 / 1%) 0px 1px 6px 4px' :
                                'rgba(12 16 27 / 15%) 0px 3px 9px 1px, rgb(12 16 27 / 10%) 0px 8px 9px 0px, rgba(12 16 27 / 8%) 0px 1px 6px 4px'
                    }
                }}
            >
                <Menu
                    keepMounted
                    anchorEl={anchorEl}
                    open={(!isHideContextMenu) && isOpen}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    onClose={() => this.handleCloseDialogFromMenu()}
                    onContextMenu={(e) => {
                        e.preventDefault()
                    }}
                >
                    {
                        contextMenuOptions.map((item) => (
                            !item.hide ?
                                <MenuItem
                                    key={item.key}
                                    onClick={() => { this.handleMenuClick(item.key as ContextMenuKey) }}>
                                    {item.label}
                                </MenuItem>
                                :
                                null
                        ))
                    }
                </Menu>
                {
                    (this.props.menu.length === 0) && !isHideAddBtn && addButtonPosition !== 'bottom' &&
                    <ButtonComponent
                        handleClick={() => {
                            this.props.handleMenuClick?.('ADD', null as any, 0)
                        }}
                        action='ADD'
                        space={false}
                    />
                }

                {
                    isFolded &&
                    <ButtonComponent
                        titleText={i18n.t('google_text_display_menu')}
                        handleClick={() => {
                            this.setState({
                                isFolded: false
                            })
                        }}
                        children={(<MenuBook />)}
                    />
                }

                {
                    !isFolded &&
                    <Box hidden={isFolded}>
                        {
                            this.renderMenuList(menu, '')
                        }
                        {
                            addButtonPosition === 'bottom' && !isHideAddBtn &&
                            <Box className="menu-list__footer-add">
                                {
                                    renderFooterAdd
                                        ? renderFooterAdd()
                                        : (
                                            <ButtonComponent
                                                handleClick={() => {
                                                    this.props.handleMenuClick?.('ADD', null as any, 0)
                                                }}
                                                action='ADD'
                                                space={false}
                                            />
                                        )
                                }
                            </Box>
                        }
                    </Box>
                }
            </Box>

        )
    }
}

// *********************
// Type
// *********************
type Props = {
    // 当前选中的菜单项
    activeMenu?: string;
    // 菜单
    menu: MenuListItem[];
    // 点击事件
    handleClick?: (menu: MenuListItem, index: string) => void;
    // 右键菜单点击
    handleMenuClick?: (key: ContextMenuKey, menu: MenuListItem, index: number) => void;
    // 拖拽完成事件
    handleOnDragEnd?: (result: DropResult, menu: FirstMenu[]) => void;
    // 其他菜单选项
    otherContextMenuOptions?: {
        label: string;
        key: string;
        hide?: boolean;
    }[]
    // 是否显示checkBox
    isShowCheckBox?: boolean;
    /**
     * 勾选态 id 列表（如菜品 `item_id`）。传入时优先于各节点上的 `checkedSecondMenuIds`，
     * 便于父组件缓存菜单树结构、仅在勾选变化时重绘勾选而不重建整树。
     */
    checkboxSelectionIds?: Array<number | string>;
    // 是否隐藏上移下移（右键菜单）并关闭拖拽排序
    isHideSort?: boolean;
    /** 为 true 时关闭拖拽（不隐藏右键里的上移/下移；与 `isHideSort` 任一为真即不可拖拽） */
    disableDrag?: boolean;
    // 是否全屏
    fullWidth?: boolean;
    // 是否隐藏右键菜单
    isHideContextMenu?: boolean;
    // 是否隐藏新增按钮
    isHideAddBtn?: boolean;
    // 是否隐藏复制
    isHideCopy?: boolean;
    style?: React.CSSProperties;
    // 是否展示副标题
    isShowSecondTitle?: boolean;
    // 是否是套餐组的操作
    isCourseGroup?: boolean;
    /**
     * 新增按钮位置：`auto` 为空列表时在列表上方显示；`bottom` 始终在列表底部显示（有数据时也显示）
     */
    addButtonPosition?: 'auto' | 'bottom';
    /** 为 true 时右键菜单不展示「新增」项（如桌台页改由底部入口新增区域） */
    hideContextMenuAdd?: boolean;
    /** 底部新增区域自定义渲染；与 `addButtonPosition="bottom"` 配合使用 */
    renderFooterAdd?: () => React.ReactNode;
    /** `plain`：无纸面底与阴影，用于嵌入桌台页等外层白卡内 */
    surfaceVariant?: 'card' | 'plain';
    /**
     * 非拖拽且当前层为纯叶子节点时，条数 ≥ 此值则用 Virtuoso 虚拟列表（默认 80）。
     * 拖拽模式不支持与 rbd 混用，仍走全量渲染。
     */
    virtualizeFlatLeavesOver?: number;
}
type State = {
    // 当前展开的项
    expandValue: string;
    /** 与 MUI Menu 的 anchorEl 一致，避免与旧版 Element 函数签名冲突 */
    anchorEl?: MenuProps['anchorEl'];
    isOpen: boolean;
    // 右键选中的菜单
    activeContextMenu?: ContextMenuItem;
    // 是否折叠
    isFolded: boolean;
}

// 菜单类型
export type MenuListItem = {
    // 唯一标识
    key: string;
    // 显示的标题
    title: string;
    // 副标题 菜名2
    secondTitle?: string;
    // children
    children?: MenuListItem[];
    // 源数据
    dataSource: any;
    // 类型
    type?: string;
    // 原始id（本地 `dmi_slu_id` / `item_id` 为 number；云端小类 `_id` 为 string）
    originId?: number | string;
    // $ 当前选中的二级菜单ids（用于菜品的复制、转移操作）
    checkedSecondMenuIds?: Array<number | string>;
    // $ 一级菜单id (操作二级菜单时需要所属一级菜单id)
    firstMenuId?: number;
    // $ 一级菜单index (操作二级菜单时需要所属一级菜单index)
    firstMenuIndex?: number;
    /** 右侧数量角标（如区域内桌台数量），Figma 桌台与区域列表 */
    trailingCount?: number;
}

// 右键菜单类型
export type ContextMenuKey = 'ADD' | 'SUB_ADD' | 'DELETE' | 'UP' | 'DOWN' | 'FOLD' | 'COPY_FIRST' | 'COPY_SECOND' | 'COPY_CAMBOGROUP' | 'TRANSFER_CAMBOGROUP'

// 右键菜单
type ContextMenuItem = MenuListItem & { index: number, isFirst?: boolean, isLast?: boolean }
